import { audiencePresets, cardLabel, tonePresets, videoPurposes } from "@veyso/core";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { Timeline } from "@veyso/core";
import { api, type LanguageView, type ProjectDetail, type ScriptResponse } from "../api.js";
import { Field, Spinner } from "../components/ui.js";
import { LanguageScript } from "./LanguageScript.js";
import { ScriptDocument, type ScriptSegment } from "./ScriptDocument.js";
import type { Filmstrip } from "./SyncPoint.js";
import type { Pane } from "./Inspector.js";

export interface ScriptPaneProps {
  projectId: string;
  timeline: Timeline;
  pane: Pane;
  onSelectPane: (pane: Pane) => void;
  onSeekSeconds: (seconds: number) => void;
  /** Persists the chosen narrator onto the BODY scene. */
  onSelectVoice: (voiceId: string | undefined) => void;
  /** Called once the server has rebuilt the timeline. */
  onRebuilt: () => void;
  /** Fetched once by the editor and shared with the timeline strip. */
  project: ProjectDetail | null;
  /** Changes when the server has rebuilt the project, so the script is refetched. */
  reloadToken?: number;
  filmstrip: Filmstrip | null;
  /** Playhead position on the finished video's clock, rounded to keep renders rare. */
  previewSeconds: number;
  /** Where BODY starts on that clock, so source times can be compared against it. */
  bodyStartSeconds: number;
  playing: boolean;
  /** Absent until the recording has been processed and there is something to cut. */
  autoCut?: {
    enabled: boolean;
    removedSeconds: number;
    takes: number;
    busy: boolean;
    error: string | null;
    /** From a run that finished without changing anything. */
    note: string | null;
    onChange: (enabled: boolean) => void;
  };
  /** The language the editor shows, when it is not the English: its words take the pane. */
  language?: LanguageView | null;
  /** Reads the languages again once one of the language's words is saved. */
  onLanguageChanged?: () => void;
}

/** How long to keep asking whether a queued job has landed before giving up. */
const POLL_MS = 2000;
const TIMEOUT_MS = 5 * 60 * 1000;

type Busy = null | "saving" | "voicing" | "rewriting";

/**
 * The script, beside the thing it narrates.
 *
 * Writing narration and editing the video are one task, not two: you change a
 * line because of what is on screen at that moment. This pane also absorbs the
 * composition rail — HEAD and END are rows in the same list — so the script
 * order and the video order are the same order.
 */
const ScriptPaneBase: React.FC<ScriptPaneProps> = ({
  projectId,
  timeline,
  pane,
  onSelectPane,
  onSeekSeconds,
  onSelectVoice,
  onRebuilt,
  project,
  reloadToken,
  autoCut,
  filmstrip,
  previewSeconds,
  bodyStartSeconds,
  playing,
  language,
  onLanguageChanged,
}) => {
  const [script, setScript] = useState<ScriptResponse | null>(null);
  const [voices, setVoices] = useState<{ id: string; name: string; category: string; freeTier: boolean }[]>([]);
  const [purpose, setPurpose] = useState<string>(project?.purpose ?? "");
  const [tone, setTone] = useState<string>(project?.tone ?? "");
  const [audience, setAudience] = useState<string>(project?.audience ?? "");
  // The project arrives after the first render, so the picker has to catch up
  // once — otherwise it sits on "No particular kind" for a project that has one.
  useEffect(() => {
    setPurpose(project?.purpose ?? "");
    setTone(project?.tone ?? "");
    setAudience(project?.audience ?? "");
  }, [project?.purpose, project?.tone, project?.audience]);

  /**
   * Saves one writing setting, reverting the control if the server refuses.
   *
   * Immediate rather than batched: these are project settings, and a dropdown
   * that silently needs a separate Save is a dropdown people believe they have
   * already used.
   */
  const saveWriting = (patch: { purpose?: string; tone?: string; audience?: string }, revert: () => void) => {
    void api.setProjectWriting(projectId, patch).catch(revert);
  };
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<Busy>(null);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [scriptResult, voiceResult] = await Promise.allSettled([api.script(projectId), api.voices(projectId)]);
      if (cancelled) return;
      if (scriptResult.status === "fulfilled") {
        setScript(scriptResult.value);
        setDrafts(Object.fromEntries(scriptResult.value.scenes.map((scene) => [scene.id, scene.script])));
      }
      // A provider that cannot list voices is not an error; the picker hides.
      if (voiceResult.status === "fulfilled") setVoices(voiceResult.value.voices);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, reloadToken]);

  /**
   * Separate from the load above, and deliberately so.
   *
   * This is a round trip to OpenAI and takes about a second. Batched with the
   * script and the voices it decided when all three appeared, so the pane a
   * user came to read sat empty waiting on a dropdown they may never open.
   * Nothing here gates anything: the control appears when the list arrives.
   */

  /** Pauses edited but not yet saved, by scene id. */
  const [pauses, setPauses] = useState<Record<string, number>>({});

  /**
   * Two questions, not one.
   *
   * `rewritten` is what needs saying again — a line whose words changed, which
   * is what the provider will be asked for. `changed` is what is unsaved at all,
   * which now includes a pause: those are silence, so they cost a rebuild and
   * not one provider call. Counting them together would price a two-second gap
   * as work someone has to pay for.
   */
  const rewritten = useMemo(
    () => (script?.scenes ?? []).filter((scene) => (drafts[scene.id] ?? scene.script).trim() !== scene.script),
    [script, drafts],
  );
  const repaused = useMemo(
    () =>
      (script?.scenes ?? []).filter(
        (scene) => (pauses[scene.id] ?? scene.pauseBefore ?? 0) !== (scene.pauseBefore ?? 0),
      ),
    [script, pauses],
  );
  const changed = useMemo(() => {
    const ids = new Set(repaused.map((scene) => scene.id));
    return [...rewritten, ...repaused.filter((scene) => !rewritten.some((one) => one.id === scene.id))].filter(
      (scene) => ids.has(scene.id) || rewritten.some((one) => one.id === scene.id),
    );
  }, [rewritten, repaused]);
  const editedIds = useMemo(() => new Set(rewritten.map((scene) => scene.id)), [rewritten]);

  const bodyDuration = timeline.sections.body.duration;

  const segments = useMemo<ScriptSegment[]>(
    () =>
      (script?.scenes ?? []).map((scene) => ({
        id: scene.id,
        seconds: scene.sourceStart ?? 0,
        text: scene.script,
        pauseBefore: pauses[scene.id] ?? scene.pauseBefore ?? 0,
      })),
    [script, pauses],
  );

  /**
   * The line being spoken right now.
   *
   * Prefers the narration actually playing, because that is what the viewer
   * hears; falls back to which part of the recording is on screen so the
   * highlight still follows before any voice has been generated.
   */
  const activeId = useMemo(() => {
    const spoken = timeline.audio.find(
      (track) =>
        track.role === "body-voice" &&
        track.enabled &&
        previewSeconds >= track.startAt &&
        previewSeconds < track.startAt + track.duration,
    );
    if (spoken) return spoken.id.replace(/^voice-/, "");

    const body = previewSeconds - bodyStartSeconds;
    if (body < 0 || body > bodyDuration) return null;
    const reached = segments.filter((segment) => segment.seconds <= body);
    return reached.length > 0 ? reached[reached.length - 1].id : null;
  }, [timeline.audio, previewSeconds, bodyStartSeconds, bodyDuration, segments]);

  /** Waits for a queued job to land. */
  const awaitJob = async () => {
    const deadline = Date.now() + TIMEOUT_MS;
    while (Date.now() < deadline) {
      await new Promise((resolve) => setTimeout(resolve, POLL_MS));
      const status = await api.status(projectId).catch(() => null);
      if (!status) continue;
      if (status.status === "failed") throw new Error(status.error ?? "The job failed. Check the worker logs.");
      if (status.status === "ready") return;
    }
    throw new Error("This is taking longer than expected — it may still finish.");
  };

  const save = async (regenerateVoice: boolean) => {
    setBusy(regenerateVoice ? "voicing" : "saving");
    setError(null);
    setNote(null);
    try {
      if (changed.length > 0 && script) {
        // Always `false`. Saving text and generating audio are queued
        // separately below, so the whole video is voiced from one place rather
        // than the server quietly voicing only the body it just edited.
        await api.updateScript(
          projectId,
          changed.map((scene) => ({
            id: scene.id,
            script: (drafts[scene.id] ?? scene.script).trim(),
            pauseBefore: pauses[scene.id] ?? scene.pauseBefore ?? 0,
          })),
          false,
        );
        setScript({
          ...script,
          scenes: script.scenes.map((scene) => ({
            ...scene,
            script: (drafts[scene.id] ?? scene.script).trim(),
            pauseBefore: pauses[scene.id] ?? scene.pauseBefore ?? 0,
          })),
        });
        setPauses({});
      }

      if (regenerateVoice) {
        // No sections: the whole video. This is the button for "narrate all of
        // it"; a single section is voiced from its own panel, which is cheaper
        // and is why those panels exist.
        await api.generateVoice(projectId, timeline.sections.body.voiceId);
      }

      if (!regenerateVoice) {
        setNote("Saved.");
      } else {
        await awaitJob();
        onRebuilt();

        // Voice generation is deliberately non-fatal on the server, and a
        // provider that rate-limits part-way through produces some clips but
        // not all. Count what landed rather than trusting that it finished.
        const rebuilt = await api.timeline(projectId);
        const made = rebuilt.data.audio.filter((track) => track.role === "body-voice").length;
        const wanted = segments.filter((segment) => segment.text.trim().length > 0).length;
        // This button narrates the cards too, so "nothing was produced" has to
        // mean nothing at all — otherwise a run that voiced both cards and no
        // body lines would report a total failure.
        const anyVoice = rebuilt.data.audio.some((track) => track.role.endsWith("-voice"));

        if (!anyVoice) setError("No narration was produced. Check the voice provider's key and the voice below.");
        else if (wanted > 0 && made < wanted)
          setError(`Only ${made} of ${wanted} lines were voiced. Generating again picks up where it stopped.`);
        else setNote("Voice generated — it replaces the recording's own audio.");
      }
      setTimeout(() => setNote(null), 6000);
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(null);
    }
  };

  /**
   * The recording itself, selectable like the cards.
   *
   * It always had a properties panel — with its length, its source and now its
   * narration — and nothing in this rail ever opened it, so the panel could
   * only be reached by accident.
   */
  const bodyRail = () => (
    <button
      onClick={() => onSelectPane("BODY")}
      data-selected={pane === "BODY"}
      className="rail-item"
      title="The recording — its properties, and re-voicing the body script"
    >
      <span className="min-w-0 flex-1 truncate text-left">
        BODY
        <span className="ml-1.5 text-ink-400">the recording</span>
      </span>
      <span className="tnum text-[10px] text-ink-400">{bodyDuration.toFixed(1)}s</span>
    </button>
  );

  /**
   * A card's row in the rail.
   *
   * A disabled card is still shown, dimmed and marked "off". Hiding the row
   * removed the only way back to the panel that turns it on again: switching
   * the card off made it, and its script, disappear from the editor entirely.
   * Only a section that does not exist has nothing to show.
   */
  const bookend = (which: "head" | "end") => {
    const scene = which === "head" ? timeline.sections.head : timeline.sections.end;
    if (!scene) return null;
    const target = which.toUpperCase() as Pane;
    const title = cardLabel(scene.content) || scene.content.kind;

    return (
      <button
        onClick={() => onSelectPane(target)}
        data-selected={pane === target}
        className={`rail-item ${scene.enabled ? "" : "opacity-45"}`}
        title={
          scene.enabled
            ? "Edit this card in the properties panel"
            : `This ${which.toUpperCase()} card is off — open it to turn it back on`
        }
      >
        <span className="min-w-0 flex-1 truncate text-left">
          <span className={scene.enabled ? "" : "line-through decoration-ink-400/60"}>{which.toUpperCase()}</span>
          <span className="ml-1.5 text-ink-400">{title || "untitled card"}</span>
        </span>
        {scene.enabled ? (
          <span className="tnum text-[10px] text-ink-400">{scene.duration.toFixed(1)}s</span>
        ) : (
          <span className="rounded-[3px] border border-[var(--edge)] px-1 py-px text-[9px] font-semibold uppercase tracking-wide text-ink-400">
            off
          </span>
        )}
      </button>
    );
  };

  const onDraftChange = useCallback(
    (id: string, text: string) => setDrafts((current) => ({ ...current, [id]: text })),
    [],
  );

  /**
   * What the folded row says.
   *
   * The two settings that change the writing most, because a summary listing
   * all five is as unreadable as the five dropdowns were.
   */
  const writingSummary =
    [videoPurposes.find((entry) => entry.id === purpose)?.label, tone].filter(Boolean).join(" · ") || "nothing set";

  // A language's words, in place of the English script. The English drafts
  // above stay in state, so switching back finds them as they were left.
  if (language) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        <div className="shrink-0 border-b border-[var(--edge)] p-3">
          <span className="eyebrow block">
            Script · {language.nativeName}
            {language.status === "review" ? " · Check before voicing" : ""}
          </span>
        </div>
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-3">
          <LanguageScript
            key={language.code}
            projectId={projectId}
            language={language}
            onChanged={onLanguageChanged ?? (() => undefined)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Pinned. These act on the whole script, so they must not scroll away
          with whichever line happens to be on screen. */}
      <div className="shrink-0 border-b border-[var(--edge)] p-3">
        <span className="eyebrow mb-2 block">Script</span>

        {/* Folded, and labelled.

            Five bare dropdowns reading "Default narrator", "Product
            walkthrough", "Clear and friendly" told you nothing about which was
            which — and they pushed the script, the thing this pane exists for,
            below the fold. Collapsed they are one row; open, every control says
            what it sets. */}
        <details className="mb-2 rounded-[6px] border border-[var(--edge)] bg-bay-850">
          {/* Stacked, not side by side: this column is ~270px, and a title and a
              summary sharing one line left both of them truncated. */}
          <summary className="cursor-pointer px-2.5 py-2 text-[12px] text-ink-200 marker:text-ink-400">
            <span className="font-medium">How it is written</span>
            <span className="mt-0.5 block text-[11px] text-ink-400">{writingSummary}</span>
          </summary>
          <div className="flex flex-col gap-2.5 border-t border-[var(--edge)] p-2.5">
            {voices.length > 0 ? (
              <Field label="Narrator">
                <select
                  className="input !py-1 text-[12px]"
                  value={timeline.sections.body.voiceId ?? ""}
                  onChange={(event) => onSelectVoice(event.target.value || undefined)}
                  title="Narrator"
                >
                  <option value="">Default narrator</option>
                  {/* One list. Splitting it by plan sorted the voices by a fact about
                  the account rather than by anything about the voice, and put a
                  heading between somebody and the name they were scanning for. */}
                  {voices.map((voice) => (
                    <option key={voice.id} value={voice.id}>
                      {voice.name}
                    </option>
                  ))}
                </select>
              </Field>
            ) : null}

            {/* What kind of video this is, which changes how the script is written
            rather than only how it sounds. Here rather than only on the upload
            screen because it is the setting you most want after reading a
            rewrite that came out as the wrong kind of video — and AI Rewrite is
            right below it. */}
            <Field label="Kind of video" hint="Changes how the script is written, not just how it sounds">
              <select
                className="input !py-1 text-[12px]"
                value={purpose}
                title="What kind of video this is — changes how the script is written"
                onChange={(event) => {
                  const next = event.target.value;
                  const previous = purpose;
                  setPurpose(next);
                  // Reverted on failure rather than left showing a choice the server
                  // never took.
                  saveWriting({ purpose: next }, () => setPurpose(previous));
                }}
              >
                <option value="">No particular kind</option>
                {videoPurposes.map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {entry.label}
                  </option>
                ))}
              </select>
            </Field>

            {/* How it should sound and who it is for. Beside the purpose because the
            three are one decision made in three parts, and until now they could
            only be set at upload — by which point nobody has seen the script
            they shape. */}
            <Field label="Tone">
              <select
                className="input !py-1 text-[12px]"
                value={tone}
                title="How the narration should sound"
                onChange={(event) => {
                  const next = event.target.value;
                  const previous = tone;
                  setTone(next);
                  saveWriting({ tone: next }, () => setTone(previous));
                }}
              >
                <option value="">No particular tone</option>
                {tonePresets.map((preset) => (
                  <option key={preset} value={preset}>
                    {preset}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Audience">
              <select
                className="input !py-1 text-[12px]"
                value={audience}
                title="Who is watching this"
                onChange={(event) => {
                  const next = event.target.value;
                  const previous = audience;
                  setAudience(next);
                  saveWriting({ audience: next }, () => setAudience(previous));
                }}
              >
                <option value="">No particular audience</option>
                {audiencePresets.map((preset) => (
                  <option key={preset} value={preset}>
                    {preset}
                  </option>
                ))}
              </select>
            </Field>

            {/* No model picker. Which model writes the script is set with
              SCRIPT_MODEL in the environment — one answer for the whole
              deployment rather than a per-project override nobody could see
              the consequences of from here. */}
          </div>
        </details>

        {/* Saving is what this pane does. Narrating moved to the panel for the
            section it narrates — one button here used to voice all three at
            once, which is why reaching an END card meant re-voicing the lot. */}
        <div className="flex gap-1.5">
          <button
            className="btn btn-secondary btn-sm flex-1"
            disabled={changed.length === 0 || busy !== null}
            onClick={() => void save(false)}
          >
            {busy === "saving"
              ? "Saving…"
              : changed.length > 0
                ? `Save ${changed.length}${rewritten.length === 0 ? " · no re-voicing" : ""}`
                : "Save"}
          </button>
          <button
            className="btn btn-primary btn-sm flex-1"
            disabled={busy !== null}
            onClick={() => void save(true)}
            title="Narrate the whole video — the HEAD card, the body script and the END card. To redo just one, use its own panel."
          >
            {busy === "voicing" ? (
              <>
                <Spinner className="!h-3 !w-3" /> Voicing
              </>
            ) : (
              "Voice all"
            )}
          </button>
        </div>

        {error ? <p className="mt-2 text-[11px] leading-snug text-signal-bad">{error}</p> : null}
        {note ? <p className="mt-2 text-[11px] leading-snug text-signal-ok">{note}</p> : null}
        {autoCut?.error ? <p className="mt-2 text-[11px] leading-snug text-signal-bad">{autoCut.error}</p> : null}
        {autoCut?.note ? <p className="mt-2 text-[11px] leading-snug text-ink-300">{autoCut.note}</p> : null}
        {autoCut?.enabled && !autoCut.busy ? (
          <p className="mt-2 text-[11px] leading-snug text-ink-400">
            {Math.round(autoCut.removedSeconds)}s of dead air removed, across {autoCut.takes}{" "}
            {autoCut.takes === 1 ? "take" : "takes"}.
          </p>
        ) : null}

        <div className="mt-2 flex flex-wrap gap-1">
          {/* Opens the panel rather than firing straight away. Rewriting throws
              the current script out and costs a model call; the prompt that
              decides the result should be in front of you before that happens,
              not discovered by pressing the button twice. */}
          <button
            onClick={() => onSelectPane("REWRITE")}
            data-selected={pane === "REWRITE"}
            disabled={!project?.transcript}
            className="rounded-[5px] border border-[var(--edge)] px-2 py-1 text-[10px] font-medium text-ink-300 transition-colors hover:bg-bay-800 hover:text-ink-100 disabled:opacity-40 data-[selected=true]:border-ember-500/50 data-[selected=true]:bg-bay-750 data-[selected=true]:text-ink-100"
            title="Show the prompt and rewrite the narration"
          >
            AI Rewrite
          </button>
          {(["TRANSITIONS", "CAPTIONS", "ACTIONS"] as const).map((entry) => (
            <button
              key={entry}
              onClick={() => onSelectPane(entry)}
              data-selected={pane === entry}
              className="rounded-[5px] border border-[var(--edge)] px-2 py-1 text-[10px] font-medium text-ink-300 transition-colors hover:bg-bay-800 hover:text-ink-100 data-[selected=true]:border-ember-500/50 data-[selected=true]:bg-bay-750 data-[selected=true]:text-ink-100"
            >
              {entry.charAt(0) + entry.slice(1).toLowerCase()}
            </button>
          ))}
        </div>
      </div>

      {/* The only thing that scrolls. */}
      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-3">
        {bookend("head")}
        {bodyRail()}

        {loading ? (
          <div className="flex items-center gap-2 py-6 text-[12px] text-ink-300">
            <Spinner /> Loading script…
          </div>
        ) : null}

        {/* The hover gap between two lines announces itself; a pause inside a
            sentence has no handle to find, so the shortcut is written down. */}
        {segments.length > 0 ? (
          <p className="pb-2 text-[10.5px] leading-relaxed text-ink-400">
            Click where you want a pause and press{" "}
            <kbd className="rounded-[3px] border border-[var(--edge)] px-1">⌘K</kbd>.
            <br />
            {/* The Undo button walks the timeline — cards, effects, backgrounds
                — and never the script, which lives here until it is saved. It
                stays greyed out while you type, correctly, and someone reaching
                for it has no way to know why. Typing is undone by the browser,
                which is better at it than a whole-document snapshot would be. */}
            <kbd className="rounded-[3px] border border-[var(--edge)] px-1">⌘Z</kbd> undoes typing while you are in a
            line. The Undo button works on the timeline, not the words.
          </p>
        ) : null}

        {!loading && segments.length === 0 ? (
          <p className="py-4 text-[11px] leading-relaxed text-ink-400">
            No script yet. It is written from the recording during processing.
          </p>
        ) : null}

        {/* Where the words came from.
            A recording nobody spoke over is narrated from what the analysis saw
            on screen rather than from a transcript, and that is worth saying:
            the lines describe the picture, so they are worth reading against it
            before the voice is generated. */}
        {segments.length > 0 && !timeline.audio.some((track) => track.role === "original") ? (
          <p className="rounded-[6px] border border-[var(--edge)] bg-bay-850 p-2.5 text-[11px] leading-relaxed text-ink-300">
            <span className="font-semibold text-ink-100">Written from the screen.</span> This recording has no
            commentary, so the narration describes what happens on it. Read it through before generating the voice.
          </p>
        ) : null}

        {segments.length > 0 ? (
          <ScriptDocument
            key={script?.id ?? "none"}
            segments={segments}
            onPauseChange={(id, seconds) => setPauses((was) => ({ ...was, [id]: seconds }))}
            durationSeconds={bodyDuration}
            filmstrip={filmstrip}
            editedIds={editedIds}
            onChange={onDraftChange}
            onSeek={onSeekSeconds}
            activeId={activeId}
            follow={playing}
          />
        ) : null}

        {bookend("end")}
      </div>
    </div>
  );
};

/** Memoised: the editor above it re-renders on every frame of playback. */
export const ScriptPane = React.memo(ScriptPaneBase);
